import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react';

import { Brush } from '@yandex-360/iconostasis/dist/icons/Brush';
import { Camera } from '@yandex-360/iconostasis/dist/icons/Camera';
import { Diagram } from '@yandex-360/iconostasis/dist/icons/Diagram';
import { Faq } from '@yandex-360/iconostasis/dist/icons/Faq';
import { Image } from '@yandex-360/iconostasis/dist/icons/Image';
import { MicroOn } from '@yandex-360/iconostasis/dist/icons/MicroOn';
import { Profile } from '@yandex-360/iconostasis/dist/icons/Profile';
import { Shield } from '@yandex-360/iconostasis/dist/icons/Shield';
import { Star } from '@yandex-360/iconostasis/dist/icons/Star';
import { useSelector } from 'react-redux';

import { DEFAULT_MODAL_Z_INDEX } from '../../../constants';
import { supportsDeviceChange } from '../../../helpers/browser-capabilities';
import { i18n } from '../../../helpers/i18n';
import { useActions } from '../../../hooks';
import { refreshDevicesListAction } from '../../settings/slice';
import { isMobileUa } from '../../uatraits';
import { SettingsSectionKey } from '../constants';
import {
    changeActiveSectionAction,
    closeSettingsModal,
    selectActiveSectionKey,
    selectIsSettingsModalOpened,
} from '../slice';
import { SettingsProps } from '../types';
import { SubmodalsManager } from './components/submodals/manager';
import { SettingsV2Desktop } from './desktop';
import { useSettingsSectionsHiddenMap } from './hooks/use-settings-sections-hidden-map';
import { SettingsV2MenuItem, SettingsV2PreprocessedMenuItem } from './types';

const MENU_ITEMS: SettingsV2PreprocessedMenuItem[] = [
    {
        id: SettingsSectionKey.Theme,
        label: () => i18n('settings-v2', 'menu-item-theme'),
        startSlot: <Brush variant="outline" size="sm" />,
    },
    {
        id: SettingsSectionKey.Account,
        label: () => i18n('settings-v2', 'menu-item-account'),
        startSlot: <Profile variant="outline" size="sm" />,
    },
    {
        id: SettingsSectionKey.Permissions,
        label: () => i18n('settings-v2', 'menu-item-permissions'),
        startSlot: <Shield variant="outline" size="sm" />,
    },
    {
        id: SettingsSectionKey.VirtualBackground,
        label: () => i18n('settings-v2', 'menu-item-virtual-background'),
        startSlot: <Image variant="outline" size="sm" />,
    },
    {
        id: SettingsSectionKey.Sound,
        label: () => i18n('settings-v2', 'menu-item-sound'),
        startSlot: <MicroOn variant="outline" size="sm" />,
    },
    {
        id: SettingsSectionKey.Camera,
        label: () => i18n('settings-v2', 'menu-item-video'),
        startSlot: <Camera variant="outline" size="sm" />,
    },
    {
        id: SettingsSectionKey.Developers,
        label: () => i18n('settings-v2', 'menu-item-develop'),
        startSlot: <Star variant="outline" size="sm" />,
    },
    {
        id: SettingsSectionKey.Help,
        label: () => i18n('settings-v2', 'menu-item-help'),
        startSlot: <Faq variant="outline" size="sm" />,
    },
    {
        id: SettingsSectionKey.Diagnostics,
        label: () => i18n('settings-v2', 'menu-item-diagnostics'),
        startSlot: <Diagram variant="outline" size="sm" />,
    },
];

const SUBMODAL_Z_INDEX = DEFAULT_MODAL_Z_INDEX + 1;

export const SettingsV2: FunctionComponent<SettingsProps> = (props) => {
    const isMobile = useSelector(isMobileUa);
    const actions = useActions({
        closeSettingsModal,
        refreshDevicesListAction,
        changeActiveSectionAction,
    });
    const isOpened = useSelector(selectIsSettingsModalOpened);
    const activeSectionKey = useSelector(selectActiveSectionKey);
    const settingsSectionsHiddenMap = useSettingsSectionsHiddenMap();

    const availableMenuItems = useMemo(
        () =>
            MENU_ITEMS.reduce((availableItems, { id, label, ...item }) => {
                if (settingsSectionsHiddenMap[id]) {
                    return availableItems;
                }

                availableItems.push({
                    ...item,
                    id,
                    label: label(),
                    testId: `menu-item-${id}`,
                });

                return availableItems;
            }, [] as SettingsV2MenuItem[]),
        [settingsSectionsHiddenMap]
    );

    const defaultSelectedItemKey = isMobile ? null : availableMenuItems[0].id;

    const handleClose = useCallback(() => {
        actions.closeSettingsModal();
    }, [actions]);

    const setActiveSectionKey = useCallback(
        (key: SettingsSectionKey | null) => {
            actions.changeActiveSectionAction(key);
        },
        [actions]
    );

    useEffect(() => {
        if (!activeSectionKey || settingsSectionsHiddenMap[activeSectionKey]) {
            setActiveSectionKey(defaultSelectedItemKey);
        }
    }, [activeSectionKey, defaultSelectedItemKey, setActiveSectionKey, settingsSectionsHiddenMap]);

    useEffect(() => {
        if (isOpened && supportsDeviceChange()) {
            actions.refreshDevicesListAction();
        }
    }, [isOpened, actions]);

    return (
        <>
            <SettingsV2Desktop
                {...props}
                zIndex={DEFAULT_MODAL_Z_INDEX}
                items={availableMenuItems}
                selectedItemId={activeSectionKey}
                onChangeSelectedItemId={setActiveSectionKey}
                visible={isOpened}
                onClose={handleClose}
            />
            <SubmodalsManager zIndex={SUBMODAL_Z_INDEX} />
        </>
    );
};
